import {
  PaymentRequestDetails,
  PaymentType,
  GpSessionId,
} from '..';
import { PaymentValidationError, ValidationErrorBag } from '../errors';
import { BaseApiService } from './base';

/**
 * GP API Version 1
 */

// Expected backend response

interface CheckPaymentResponse {
  validated: {
    status: string;
    [key: string]: any;
  };
}

export class ApiService extends BaseApiService {

  /**
   * Fetch the Global Payments access token and attach to the form.
   */
  async fetchAccessToken(): Promise<string> {
    try {
      const response = await this.axiosInstance.post(`/pay/accesstoken?permissions=PMT_POST_Create_Single&expires_in=10_MINUTES`);
      
      // Returned token
      return response.data.token;

    } catch (error) {
      throw new Error("Could not initialize payment security.");
    }
  }

  /**
   * Check the fields for a given payment request are valid.
   */
  async checkPaymentFields(paymentRequestData: PaymentRequestDetails) {
    const { paymentType, fields } = paymentRequestData;
    const requestData = { fields: { ...fields } };

    try {
      const response = await this.axiosInstance.post<CheckPaymentResponse>(
        `/pay/${paymentType}/check`,
        requestData
      );
      const { validated } = response.data;
      return {
        validated: response.data.validated,
        accessToken: response.data.validated.access_token,
      };
    } catch (error: any) {
      // Handle CORS/Network failure
      if (error.code === 'ERR_NETWORK') {
        return { validated: { status: 'mocked_bypass' } };
      }

      // Handle Validation errors (422)
      if (error.response?.status === 422) {
        const errorBag = error.response.data as ValidationErrorBag;
        throw new PaymentValidationError(errorBag);
      }

      throw error;
    }
  }

  /**
   * Identify payment type and submit the Global Payments payment
   * The server will attempt to automatically capture the payment if possible,
   * so this call may result in immediate success.
   */
  async submitPayment(
    requestDetails: PaymentRequestDetails,
    paymentToken: string,
    name?: string,
    email?: string,
    walletType?: 'google-pay' | 'apple-pay',
    serverTransactionId?: string,
    authenticationValue?: string,
    eci?: string,
    messageVersion?: string,
    acsTransactionId?: string,
  ) {

    const { amount, paymentType, fields } = requestDetails;

    const requestData = {
      amount,
      name: name,
      email: email || 'webteamsbgl@gmail.com',
      payment_token: paymentToken,
      wallet_type: walletType || null,
      fields: { ...fields },
      serverTransactionId: serverTransactionId || null,
      authentication_value: authenticationValue || null,
      eci: eci || null,
      message_version: messageVersion || null,
      acsTransactionId: acsTransactionId || null,
    };

    try {
      const response = await this.axiosInstance.post(
        `/pay/${paymentType}/start`,
        requestData
      );

      const {
        message,
        transaction_key,
        payment_method_id,
        requires_action,
        success,
      } = response.data;

      return {
        message,
        transactionKey: transaction_key,
        paymentMethodId: payment_method_id,
        requiresAction: requires_action,
        success,
      };
    } catch (error: any) {
      if (error.response?.status === 422) {

        throw new PaymentValidationError(
          error.response.data as ValidationErrorBag
        );
      }
      throw error;
    }
  }

  /**
   * Continue an existing payment using a Global Payments Payment Token.
   */
 async continueExistingPayment(
    paymentType: PaymentType,
    paymentToken: string,
    transactionKey: GpSessionId,
    additional: {
      cres?: string;
      server_transaction_id?: string;
      authentication_value?: string;
      eci?: string;
      message_version?: string;
      [key: string]: any;
    } = {}
  ) {
    
    // Normalize properties so the backend receives both camelCase and snake_case variants if needed
    const serverTxId = additional.server_transaction_id;

    const requestData = {
      ...additional,
      transaction_key: transactionKey,
      payment_token: paymentToken,
      server_transaction_id: serverTxId,
      cres: additional.cres,
      authentication_value: additional.authentication_value,
      eci: additional.eci,
      message_version: additional.message_version,
    };

    const response = await this.axiosInstance
      .post(`/pay/${paymentType}/continue`, requestData)
      .catch((error) => {
        if (error.response?.status === 422) {
          const errorBag = error.response.data as ValidationErrorBag;
          throw new PaymentValidationError(errorBag);
        }

        throw error;
      });

    const {
      message,
      transaction_key,
      payment_method_id,
      requires_action,
      success,
    } = response.data;

    return {
      message,
      transactionKey: transaction_key,
      paymentMethodId: payment_method_id,
      requiresAction: requires_action,
      success,
    };
  }
}